import React, { useRef, useState } from 'react';
import {
  extractApiErrorMessage,
  fileToBase64,
  getBucketUrlFile,
  uploadTempFile,
} from '@/utils';
import { showSuccessToast, showErrorToast } from '@/components/toast/globalToast';
import { v4 as uuidv4 } from 'uuid';
import FilePreviewCard from '@/components/FilePreviewCard/FilePreviewCard';

interface PdfUploadProps {
  /** Uploaded file URL (temp bucket URL). Empty string when nothing is uploaded. */
  value: string;
  /**
   * Called after a successful upload with the temp-bucket URL, the original
   * file name and its size in bytes.
   */
  onUploaded: (url: string, fileName: string, fileSize: number) => void;
  /** Called when the user clears the uploaded file. */
  onRemove: () => void;
  /**
   * Shared-upload file_type enum value. The upstream service only accepts a
   * fixed set; handover documents ride in under 'vehicleHandover'.
   */
  fileType?: string;
  /** Validation error message to render under the control. */
  error?: string;
  /** Optional display name to show when `value` is prefilled (e.g. edit mode). */
  fileName?: string;
  /**
   * A legacy, non-GCS document link from an existing record (e.g. a pasted
   * Google Drive URL). When set and there is no uploaded `value`, the control
   * shows a "please re-upload" notice with a link to the old document — that
   * external link can't be migrated into GCS, so the user must upload a PDF.
   */
  legacyExternalUrl?: string;
  disabled?: boolean;
  className?: string;
}

const MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Direct PDF upload control — mirrors the STNK uploader in AddVehicleModal.
 * The PDF is pushed to the shared temp bucket and the resulting URL is handed
 * back via `onUploaded`; the backend relocates it into its final bucket on save.
 */
const PdfUpload: React.FC<PdfUploadProps> = ({
  value,
  onUploaded,
  onRemove,
  fileType = 'vehicleHandover',
  error,
  fileName,
  legacyExternalUrl,
  disabled = false,
  className = '',
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [localFile, setLocalFile] = useState<{
    name: string;
    size: number;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Prefer the freshly-uploaded file's metadata; otherwise derive a display
  // name from a prefilled URL (edit mode) so the user sees something sensible.
  const displayName =
    localFile?.name ||
    fileName ||
    (value ? decodeURIComponent(value.split('/').pop() || 'document.pdf') : '');

  const handleSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    // Allow re-selecting the same file after a remove.
    event.target.value = '';
    if (!file) return;

    if (file.type !== 'application/pdf') {
      showErrorToast('Error', 'Only PDF files are allowed');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      showErrorToast('Error', 'File size must be under 10 MB');
      return;
    }

    try {
      setIsUploading(true);
      const uuid = uuidv4();
      const bucketRes = await getBucketUrlFile({
        uuid,
        fileType,
        fileFormat: 'pdf',
      });
      if (!bucketRes?.data) {
        throw new Error('Failed to get upload URL');
      }
      const url = bucketRes.data;
      const base64Data = await fileToBase64(file);
      const uploadRes = await uploadTempFile(
        url,
        base64Data,
        file.name,
        file.type
      );
      if (uploadRes.status !== 200) {
        throw new Error('Upload failed');
      }

      setLocalFile({ name: file.name, size: file.size });
      onUploaded(url, file.name, file.size);
      showSuccessToast('Success', 'Upload document success');
    } catch (err) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(err, 'Failed to upload document')
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setLocalFile(null);
    onRemove();
  };

  return (
    <div className={className}>
      <div className="border-2 border-dashed border-gray-300 rounded-lg w-full">
        {isUploading ? (
          <div className="p-8 flex flex-col justify-center items-center text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
            <p className="mt-2 text-sm text-gray-600">Uploading document...</p>
          </div>
        ) : !value ? (
          <div className="p-8 flex flex-col justify-center items-center text-center">
            <div className="text-gray-400 text-4xl mb-4">
              <i className="ri-file-pdf-2-line" />
            </div>
            <p className="font-semibold text-gray-700">Upload document</p>
            <p className="text-xs text-gray-500 mt-1">PDF format, up to 10 MB.</p>
            <button
              type="button"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              className="mt-4 bg-white border border-gray-300 text-gray-700 text-sm font-semibold py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Browse File
            </button>
          </div>
        ) : (
          <FilePreviewCard
            fileName={displayName}
            fileSize={localFile?.size}
            onRemove={disabled ? undefined : handleRemove}
            className="border-0"
          />
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={handleSelect}
      />
      {!value && legacyExternalUrl && (
        <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <i className="ri-error-warning-line mt-0.5 text-sm" />
          <span>
            The previous document is an external link and can&apos;t be
            migrated. Please re-upload it as a PDF.{' '}
            <a
              href={legacyExternalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline hover:text-amber-800 break-all"
            >
              View previous document
            </a>
          </span>
        </div>
      )}
      {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default PdfUpload;
